import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProjectSelector } from '../components/ProjectSelector';
import { PipelineSummary } from '../components/PipelineSummary';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { ChatPanel } from '../components/ChatPanel';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { SessionListPanel } from '../components/SessionListPanel';
import type { SessionRow, SessionSection } from '../components/SessionListPanel';
import { useToast } from '../components/ui/Toast';
import { UsageBadge } from '../components/UsageBadge';
import { Markdown } from '../components/ui/Markdown';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import type { Project, PlanningSession, DaedalusSession, DaedalusSessionStatus, DaedalusRunnerStatus, DaedalusTicket, TicketDraft, JiraTicketRef } from '@pantheon/shared';

/** Runner considered offline if the heartbeat is older than 3 poll intervals */
const RUNNER_STALE_MS = 3 * 60_000;

function RunnerChip({ status }: { status: DaedalusRunnerStatus | null }) {
  if (!status || status.runnerMode !== 'local') return null;

  // One chip per runner; stale heartbeats older than a day drop off entirely
  const runners = [...status.runners]
    .filter((r) => Date.now() - new Date(r.lastSeenAt).getTime() < 24 * 60 * 60_000)
    .sort((a, b) => a.runnerId.localeCompare(b.runnerId));

  if (runners.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border bg-red-50 text-red-700 border-red-200">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        No runner connected
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {runners.map((runner) => {
        const online = Date.now() - new Date(runner.lastSeenAt).getTime() < RUNNER_STALE_MS;
        const chip = !online
          ? { dot: 'bg-red-500', color: 'bg-red-50 text-red-700 border-red-200', text: `"${runner.runnerId}" offline` }
          : runner.status === 'working'
            ? { dot: 'bg-blue-500 animate-pulse', color: 'bg-blue-50 text-blue-700 border-blue-200', text: `"${runner.runnerId}" working` }
            : runner.status === 'rate_limited'
              ? { dot: 'bg-amber-500', color: 'bg-amber-50 text-amber-700 border-amber-200', text: `"${runner.runnerId}" rate limited` }
              : { dot: 'bg-green-500', color: 'bg-green-50 text-green-700 border-green-200', text: `"${runner.runnerId}" online` };
        return (
          <span key={runner.runnerId} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${chip.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${chip.dot}`} />
            {chip.text}
          </span>
        );
      })}
    </span>
  );
}

const ticketStatusConfig: Record<string, { text: string; color: string }> = {
  pending: { text: 'Pending', color: 'text-gray-500' },
  implementing: { text: 'Working...', color: 'text-blue-500' },
  completed: { text: 'Done', color: 'text-green-600' },
  failed: { text: 'Failed', color: 'text-red-500' },
  skipped: { text: 'Epic (auto)', color: 'text-purple-500' },
  developer: { text: 'Developer', color: 'text-amber-600' },
};

// status → display for the session list panel (mirrors StatusBadge color conventions)
const sessionStatusDisplay: Record<
  DaedalusSessionStatus,
  { text: string; badge: string; dot: string }
> = {
  pending: { text: 'Pending', badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  cloning: { text: 'Cloning', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  implementing: { text: 'Implementing', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  pushing: { text: 'Pushing', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  revising: { text: 'Revising', badge: 'bg-blue-50 text-blue-600', dot: 'bg-blue-400 animate-pulse' },
  waiting_for_input: { text: 'Waiting for input', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400' },
  pr_created: { text: 'PR created', badge: 'bg-primary-light text-primary', dot: 'bg-primary' },
  in_review: { text: 'In review', badge: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-400' },
  completed: { text: 'Completed', badge: 'bg-success-light text-success', dot: 'bg-green-400' },
  failed: { text: 'Failed', badge: 'bg-danger-light text-danger', dot: 'bg-red-400' },
};

const SESSION_GROUPS: { key: string; title: string; statuses: DaedalusSessionStatus[] }[] = [
  { key: 'pending', title: 'Pending', statuses: ['pending'] },
  { key: 'in_progress', title: 'In Progress', statuses: ['cloning', 'implementing', 'pushing', 'revising'] },
  { key: 'waiting_for_input', title: 'Waiting for Input', statuses: ['waiting_for_input'] },
  { key: 'in_review', title: 'In Review', statuses: ['pr_created', 'in_review'] },
  { key: 'completed', title: 'Completed', statuses: ['completed'] },
  { key: 'failed', title: 'Failed', statuses: ['failed'] },
];

export function DaedalusPage() {
  const { get, post, del } = useApi();
  const { toast } = useToast();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [availableSessions, setAvailableSessions] = useState<PlanningSession[]>([]);
  const [daedalusSessions, setDaedalusSessions] = useState<DaedalusSession[]>([]);
  const [activeSession, setActiveSession] = useState<DaedalusSession | null>(null);
  const [expandedHermesId, setExpandedHermesId] = useState<string | null>(null);
  const [hermesTickets, setHermesTickets] = useState<TicketDraft[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [closeJira, setCloseJira] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [runnerStatus, setRunnerStatus] = useState<DaedalusRunnerStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable refs
  const getRef = useRef(get);
  const selectedProjectRef = useRef(selectedProject);
  const deletedDaedalusIdsRef = useRef<Set<string>>(new Set());
  getRef.current = get;
  selectedProjectRef.current = selectedProject;
  const delRef = useRef(del);
  delRef.current = del;

  // Fetch available Hermes sessions and existing Daedalus sessions
  const fetchData = useCallback(async (projId: string) => {
    const [avail, existing] = await Promise.all([
      getRef.current<PlanningSession[]>(`/daedalus/available-sessions?projectId=${projId}`),
      getRef.current<DaedalusSession[]>(`/daedalus/sessions?projectId=${projId}`),
    ]);
    if (avail.success && avail.data) setAvailableSessions(avail.data);
    if (existing.success && existing.data) {
      setDaedalusSessions(existing.data.filter((s) => !deletedDaedalusIdsRef.current.has(s.daedalusSessionId)));
    }
  }, []);

  useEffect(() => {
    if (selectedProject) fetchData(selectedProject.projectId);
  }, [selectedProject, fetchData]);

  // Runner heartbeat chip — refresh every 30s
  useEffect(() => {
    const fetchStatus = async () => {
      const result = await getRef.current<DaedalusRunnerStatus>('/daedalus/runner-status');
      if (result.success && result.data) setRunnerStatus(result.data);
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 8000);
    return () => clearInterval(interval);
  }, []);

  const isLocalMode = runnerStatus?.runnerMode === 'local';

  // Stable refs so focus/interval refetch always see the latest values.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const activeSessionRef = useRef(activeSession);
  activeSessionRef.current = activeSession;

  // Refresh the lists + the open session when the user returns to the tab/window, so a
  // session that changed while they were away (e.g. got merged) shows its real status
  // instead of freezing at the last polled value.
  const refetchAll = useCallback(() => {
    const proj = selectedProjectRef.current;
    if (proj) void fetchDataRef.current(proj.projectId);
    const active = activeSessionRef.current;
    if (active) {
      void getRef.current<DaedalusSession>(`/daedalus/sessions/${active.daedalusSessionId}`).then((r) => {
        if (r.success && r.data) setActiveSession(r.data);
      });
    }
  }, []);
  useRefetchOnFocus(refetchAll);

  // Deep linking: /daedalus/:sessionId loads that session; selecting one updates the URL.
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlSessionId || urlSessionId === loadedIdRef.current) return;
    loadedIdRef.current = urlSessionId;
    void getRef.current<DaedalusSession>(`/daedalus/sessions/${urlSessionId}`).then(async (r) => {
      if (!r.success || !r.data) return;
      setActiveSession(r.data);
      // Also select the session's project so the page shows the session detail (not the
      // cross-project summary) and loads its project-scoped data (session list, runner status).
      if (r.data.projectId) {
        const p = await getRef.current<Project>(`/projects/${r.data.projectId}`);
        if (p.success && p.data) setSelectedProject(p.data);
      }
    });
  }, [urlSessionId]);

  const clearActive = useCallback(() => {
    setActiveSession(null);
    loadedIdRef.current = null;
    navigate('/daedalus');
  }, [navigate]);

  // Keep the session lists fresh while a project is open (statuses change on the backend).
  useEffect(() => {
    if (!selectedProject) return;
    const id = setInterval(() => fetchDataRef.current(selectedProject.projectId), 8000);
    return () => clearInterval(id);
  }, [selectedProject]);

  // Poll the open session. Keep polling even after a "terminal" status — just slower —
  // so a status that changes on the backend (a merge, a reset) still updates the view.
  useEffect(() => {
    if (!activeSession) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const terminal = ['completed', 'in_review', 'pr_created', 'failed'].includes(activeSession.status);
    const poll = async () => {
      const result = await getRef.current<DaedalusSession>(`/daedalus/sessions/${activeSession.daedalusSessionId}`);
      if (result.success && result.data) setActiveSession(result.data);
    };
    poll();
    pollRef.current = setInterval(poll, terminal ? 20_000 : 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeSession?.daedalusSessionId, activeSession?.status]);

  const handleRemoveAvailable = async (hermesSession: PlanningSession, closeJiraTickets: boolean) => {
    setRemoving(true);
    const result = await post(`/daedalus/available-sessions/${hermesSession.sessionId}/dismiss`, { closeJiraTickets });
    setRemoving(false);
    if (result.success) {
      setRemovingId(null);
      setAvailableSessions((prev) => prev.filter((s) => s.sessionId !== hermesSession.sessionId));
      toast(closeJiraTickets ? "Removed — closing its Jira tickets as Won't Do" : 'Removed from Ready for Implementation', 'success');
    } else {
      toast(result.error?.message ?? 'Failed to remove', 'error');
    }
  };

  const handleSendToDaedalus = async (hermesSession: PlanningSession) => {
    if (!selectedProject) return;
    setSending(true);
    const result = await post<{ sessions: DaedalusSession[] }>('/daedalus/sessions', {
      hermesSessionId: hermesSession.sessionId,
      projectId: selectedProject.projectId,
    });
    const sessions = result.success ? result.data?.sessions : undefined;
    if (sessions?.length) {
      setActiveSession(sessions[0]);
      setAvailableSessions((prev) => prev.filter((s) => s.sessionId !== hermesSession.sessionId));
      toast(
        sessions.length > 1
          ? `Queued ${sessions.length} parallel workstream sessions`
          : isLocalMode ? 'Queued for the runner' : 'Sent to Daedalus',
        'success',
      );
    } else {
      toast(result.error?.message ?? 'Failed', 'error');
    }
    void refetchAll();
    setSending(false);
  };

  const handleExpandHermesSession = async (sessionId: string) => {
    if (expandedHermesId === sessionId) {
      setExpandedHermesId(null);
      setHermesTickets([]);
      return;
    }
    setExpandedHermesId(sessionId);
    setLoadingTickets(true);
    const result = await get<any>(`/hermes/sessions/${sessionId}`);
    if (result.success && result.data?.output?.tickets) {
      setHermesTickets(result.data.output.tickets);
    }
    setLoadingTickets(false);
  };

  const handleLoadSession = (sessionId: string) => {
    navigate(`/daedalus/${sessionId}`);
  };

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      if (!confirm('Delete this Daedalus session?')) return;
      await delRef.current(`/daedalus/sessions/${sessionId}`);
      deletedDaedalusIdsRef.current.add(sessionId);
      setDaedalusSessions((prev) => prev.filter((s) => s.daedalusSessionId !== sessionId));
      toast('Session deleted', 'info');
    },
    [toast],
  );

  // Group Daedalus sessions into per-status sections for the panel. The list is always
  // project-scoped here, so no project chip is needed.
  const toRow = useCallback(
    (ds: DaedalusSession): SessionRow => {
      const cfg = sessionStatusDisplay[ds.status] ?? sessionStatusDisplay.pending;
      const title = ds.tickets[0]?.title ?? ds.branch ?? 'Session';
      return {
        id: ds.daedalusSessionId,
        title,
        statusText: cfg.text,
        statusBadge: cfg.badge,
        statusDot: cfg.dot,
        createdAt: ds.createdAt,
        updatedAt: ds.updatedAt,
        meta: `${ds.tickets.length} ticket${ds.tickets.length === 1 ? '' : 's'}`,
        onDelete: () => handleDeleteSession(ds.daedalusSessionId),
      };
    },
    [handleDeleteSession],
  );

  const sections = useMemo<SessionSection[]>(() => {
    const byRecent = (a: DaedalusSession, b: DaedalusSession) =>
      new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime();
    return SESSION_GROUPS.map((g) => ({
      key: g.key,
      title: g.title,
      rows: daedalusSessions
        .filter((s) => g.statuses.includes(s.status))
        .sort(byRecent)
        .map(toRow),
    }));
  }, [daedalusSessions, toRow]);

  const handleRetrieveComments = async () => {
    if (!activeSession) return;
    setLoading(true);
    await post(`/daedalus/sessions/${activeSession.daedalusSessionId}/retrieve-comments`, {});
    void refetchAll();
    toast('Retrieving comments and making revisions...', 'info');
    setLoading(false);
  };

  const isProcessing = activeSession && ['pending', 'cloning', 'implementing', 'pushing', 'revising'].includes(activeSession.status);

  return (
    <div className="max-w-5xl animate-rise">
      <PageHeader
        mark="D"
        markBg="bg-daedalus/12"
        markText="text-daedalus"
        title="Daedalus"
        subtitle="Pick up tickets and implement code changes."
        actions={<RunnerChip status={runnerStatus} />}
      />

      {/* Project selector */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Project</label>
        <ProjectSelector value={selectedProject?.projectId} onChange={(p) => { setSelectedProject(p); clearActive(); }} />
      </div>

      {!selectedProject && !activeSession && (
        <PipelineSummary
          stages={['implementing', 'developer', 'review']}
          title="Implementation across all projects"
          emptyText="Nothing being implemented right now — pick a project below to queue work."
        />
      )}

      {selectedProject && !activeSession && (
        <>
          {/* Existing Daedalus sessions — grouped per status, minus Completed
              (rendered below Ready for Implementation so the order reads
              Pending → In Progress → Waiting → In Review → Failed → Ready → Completed). */}
          {daedalusSessions.length > 0 && (
            <SessionListPanel
              sections={sections.filter((s) => s.key !== 'completed')}
              onSelect={handleLoadSession}
              className="mb-6"
            />
          )}

          {/* Available Hermes sessions */}
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Ready for Implementation</h3>
          {availableSessions.length === 0 ? (
            <EmptyState title="No approved sessions ready to implement" hint="Create and approve tickets in Hermes first — they'll show up here." />
          ) : (
            <div className="space-y-2">
              {availableSessions.map((hs) => {
                const isExpanded = expandedHermesId === hs.sessionId;
                const ticketCount = hs.jiraResults?.createdTickets.filter((t: JiraTicketRef) => t.status === 'created').length ?? 0;

                return (
                  <div key={hs.sessionId} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
                    {/* Header — click to expand */}
                    <div
                      onClick={() => handleExpandHermesSession(hs.sessionId)}
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <svg
                          className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm text-gray-700 line-clamp-1">{hs.input.idea.substring(0, 120)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{ticketCount} tickets</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setRemovingId(hs.sessionId); setCloseJira(true); }}
                        >
                          Remove
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleSendToDaedalus(hs); }}
                          loading={sending}
                        >
                          {isLocalMode ? 'Queue for Runner' : 'Send to Daedalus'}
                        </Button>
                      </div>
                    </div>

                    {/* Remove confirmation */}
                    {removingId === hs.sessionId && (
                      <div className="border-t border-gray-100 bg-red-50/50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-gray-700">
                          <p className="font-medium">Remove this plan from Ready for Implementation?</p>
                          <p className="text-xs text-gray-500 mt-0.5">The plan stays in Hermes; Daedalus won't implement it.</p>
                          {ticketCount > 0 && (
                            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                              <input type="checkbox" checked={closeJira} onChange={(e) => setCloseJira(e.target.checked)} />
                              Also mark its {ticketCount} Jira ticket{ticketCount === 1 ? '' : 's'} Won't Do
                            </label>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => setRemovingId(null)}>Cancel</Button>
                          <Button size="sm" variant="danger" loading={removing} onClick={() => handleRemoveAvailable(hs, ticketCount > 0 && closeJira)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expanded ticket details */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 pb-4">
                        {loadingTickets ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                            <Spinner size="sm" /> Loading tickets...
                          </div>
                        ) : hermesTickets.length === 0 ? (
                          <p className="text-sm text-gray-500 py-4">No ticket details available.</p>
                        ) : (
                          <table className="w-full text-sm mt-3">
                            <thead className="text-xs text-gray-500 uppercase">
                              <tr>
                                <th className="text-left pb-2">Type</th>
                                <th className="text-left pb-2">Title</th>
                                <th className="text-left pb-2">Priority</th>
                                <th className="text-left pb-2">AC</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {hermesTickets.sort((a, b) => a.order - b.order).map((ticket) => {
                                const typeColors: Record<string, string> = {
                                  epic: 'bg-purple-100 text-purple-700',
                                  story: 'bg-green-100 text-green-700',
                                  task: 'bg-blue-100 text-blue-700',
                                  subtask: 'bg-cyan-100 text-cyan-700',
                                  bug: 'bg-red-100 text-red-700',
                                };
                                return (
                                  <tr key={ticket.draftId} className="hover:bg-gray-50">
                                    <td className="py-2 pr-3">
                                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full uppercase ${typeColors[ticket.type] ?? 'bg-gray-100 text-gray-600'}`}>
                                        {ticket.type}
                                      </span>
                                    </td>
                                    <td className="py-2 pr-3">
                                      <p className="text-gray-700 font-medium">{ticket.title}</p>
                                      <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{ticket.description.substring(0, 150)}</p>
                                    </td>
                                    <td className="py-2 pr-3 text-xs text-gray-500 capitalize">{ticket.priority}</td>
                                    <td className="py-2 text-xs text-gray-500">{ticket.acceptanceCriteria.length} items</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Completed sessions — below Ready for Implementation, gated on having rows */}
          {(() => {
            const completed = sections.find((s) => s.key === 'completed');
            if (!completed || completed.rows.length === 0) return null;
            return (
              <SessionListPanel
                sections={[completed]}
                onSelect={handleLoadSession}
                className="mt-6"
              />
            );
          })()}
        </>
      )}

      {/* Active Daedalus session */}
      {activeSession && (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StatusBadge status={activeSession.status} />
              <span className="text-sm font-mono text-gray-500">
                {activeSession.branch}
                {activeSession.baseBranch && (
                  <span className="text-purple-500"> → {activeSession.baseBranch}</span>
                )}
              </span>
              <UsageBadge usage={activeSession.usage} />
              {activeSession.engine && (
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                    activeSession.engine === 'pty'
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-slate-100 text-slate-700 border-slate-200'
                  }`}
                  title={activeSession.engine === 'pty'
                    ? 'This session ran on the interactive Claude Code terminal (node-pty), not headless claude -p'
                    : 'This session ran on the headless claude -p (print) engine'}
                >
                  {activeSession.engine === 'pty' ? 'PTY engine' : 'claude -p'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {activeSession.prs?.length ? (
                activeSession.prs.map((pr) => (
                  <a key={pr.repoLabel} href={pr.prUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                    {activeSession.prs!.length > 1 ? `PR (${pr.repoLabel})` : 'View PR'}
                  </a>
                ))
              ) : activeSession.prUrl ? (
                <a href={activeSession.prUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                  View PR
                </a>
              ) : null}
              <Button variant="danger" size="sm" onClick={async () => {
                if (!confirm('Delete this Daedalus session?')) return;
                await delRef.current(`/daedalus/sessions/${activeSession.daedalusSessionId}`);
                deletedDaedalusIdsRef.current.add(activeSession.daedalusSessionId);
                setDaedalusSessions((prev) => [...prev.filter((s) => s.daedalusSessionId !== activeSession.daedalusSessionId)]);
                clearActive();
                toast('Session deleted', 'info');
              }}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={clearActive}>Back</Button>
            </div>
          </div>

          {/* Processing spinner — hidden while dependency-blocked (nothing is running) */}
          {isProcessing && !(activeSession.status === 'pending' && activeSession.dependsOnHermes?.length) && (
            <div className="flex flex-col items-center py-8 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
              <Spinner size="lg" className="text-primary mb-4" />
              <p className="text-base font-medium text-gray-700">
                {activeSession.status === 'cloning' && 'Cloning repository...'}
                {activeSession.status === 'implementing' && 'Implementing tickets...'}
                {activeSession.status === 'pushing' && 'Pushing to Bitbucket...'}
                {activeSession.status === 'revising' && 'Making revisions...'}
                {activeSession.status === 'pending' && 'Starting up...'}
              </p>
            </div>
          )}

          {/* Athena wave ordering — queued behind dependency workstreams */}
          {activeSession.status === 'pending' && !!activeSession.dependsOnHermes?.length && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <span className="font-semibold">Waiting on {activeSession.dependsOnHermes.length} dependency workstream(s) — not started.</span>{' '}
              No runner will pick this session up until each dependency&apos;s Daedalus session is completed
              (PR merged + Marked Done &amp; Merged). It then starts automatically.
            </div>
          )}

          {/* Daedalus is blocked on a question */}
          {activeSession.status === 'waiting_for_input' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-800 mb-1">Daedalus has a question</p>
              <Markdown className="text-amber-900">{activeSession.pendingQuestion?.question ?? 'See the chat below.'}</Markdown>
              <p className="text-xs text-amber-600 mt-2">
                Answer in the chat below (or comment on the Jira ticket) — Daedalus continues automatically.
              </p>
            </div>
          )}

          {/* Tester summary — runner-generated release notes for this session */}
          {activeSession.testerSummary && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-gray-50">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Tester summary</h3>
                  <p className="text-xs text-gray-500">
                    Release notes for this session — what changed and what to verify.
                    {activeSession.testerSummaryAt ? ` · updated ${new Date(activeSession.testerSummaryAt).toLocaleString()}` : ''}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  void navigator.clipboard?.writeText(activeSession.testerSummary ?? '');
                  toast('Summary copied', 'info');
                }}>Copy</Button>
              </div>
              <div className="px-5 py-4 text-sm text-gray-700">
                <Markdown>{activeSession.testerSummary}</Markdown>
              </div>
            </div>
          )}

          {/* Ticket progress table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Ticket</th>
                  <th className="px-4 py-3 text-left">Title</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Files</th>
                  <th className="px-4 py-3 text-left"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeSession.tickets.map((ticket) => {
                  const ts = ticketStatusConfig[ticket.status] ?? ticketStatusConfig.pending;
                  return (
                    <tr key={ticket.jiraKey} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <a href={ticket.jiraUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-mono text-xs">
                          {ticket.jiraKey}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{ticket.title}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${ts.color}`}>
                          {ticket.status === 'implementing' && <Spinner size="sm" className="inline mr-1" />}
                          {ts.text}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {ticket.filesChanged?.join(', ') ?? '-'}
                      </td>
                      <td className="px-4 py-3">
                        {ticket.status === 'developer' && ['in_review', 'pr_created', 'failed'].includes(activeSession.status) && (
                          <button
                            className="text-xs text-primary hover:underline whitespace-nowrap"
                            onClick={async () => {
                              await post(`/daedalus/sessions/${activeSession.daedalusSessionId}/reroute-ticket`, {
                                jiraKey: ticket.jiraKey,
                                routing: 'daedalus',
                              });
                              const r = await getRef.current<DaedalusSession>(`/daedalus/sessions/${activeSession.daedalusSessionId}`);
                              if (r.success && r.data) setActiveSession(r.data);
                              void refetchAll();
                              toast('Ticket assigned to Daedalus', 'info');
                            }}
                          >
                            Assign to Daedalus
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chat */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Activity</h3>
            <ChatPanel
              messages={activeSession.messages}
              onSend={async (msg, attachments) => {
                if (!activeSession) return;
                // Show user message immediately
                setActiveSession({
                  ...activeSession,
                  messages: [...activeSession.messages, { role: 'user', content: msg, timestamp: new Date().toISOString(), attachments }],
                });
                if (activeSession.status === 'waiting_for_input') {
                  // Answer to Daedalus's question — the local runner picks it up and resumes
                  await post(`/daedalus/sessions/${activeSession.daedalusSessionId}/messages`, { message: msg, attachments });
                } else {
                  await post(`/daedalus/sessions/${activeSession.daedalusSessionId}/retrieve-comments`, { chatMessage: msg, attachments });
                }
                void refetchAll();
              }}
              loading={!!isProcessing}
              disabled={!['in_review', 'pr_created', 'failed', 'waiting_for_input'].includes(activeSession.status)}
              agentName="Daedalus"
            />
          </div>

          {/* Stuck revision — show cancel button */}
          {['revising', 'pending', 'cloning', 'implementing', 'pushing', 'waiting_for_input'].includes(activeSession.status) && (
            <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 px-4 py-3">
              <span className="text-sm text-gray-500">
                {activeSession.status === 'revising' ? 'Daedalus is revising...' : `Status: ${activeSession.status}`}
              </span>
              <Button variant="danger" size="sm" onClick={async () => {
                await post(`/daedalus/sessions/${activeSession.daedalusSessionId}/reset-status`, {});
                const result = await getRef.current<DaedalusSession>(`/daedalus/sessions/${activeSession.daedalusSessionId}`);
                if (result.success && result.data) setActiveSession(result.data);
                toast('Status reset to In Review', 'info');
              }}>
                Cancel / Reset Status
              </Button>
            </div>
          )}

          {/* Review actions */}
          {['in_review', 'pr_created', 'failed'].includes(activeSession.status) && (() => {
            const allDone = activeSession.tickets.every(
              (t) => ['completed', 'skipped', 'developer'].includes(t.status),
            );
            const hasPendingDaedalus = activeSession.tickets.some(
              (t) => t.status === 'pending',
            );
            return (
              <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 px-4 py-3">
                <span className="text-sm text-gray-500">
                  {hasPendingDaedalus
                    ? 'Tickets assigned to Daedalus are pending — run revisions to implement them.'
                    : 'Review the PR and leave comments on the Jira tickets for revisions.'}
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={handleRetrieveComments} loading={loading}>
                    {hasPendingDaedalus ? 'Run Daedalus' : 'Retrieve Comments'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!allDone}
                    onClick={async () => {
                      const result = await post<{ status: string }>(`/daedalus/sessions/${activeSession.daedalusSessionId}/mark-merged`, {});
                      if (result.success) {
                        const refreshed = await getRef.current<DaedalusSession>(`/daedalus/sessions/${activeSession.daedalusSessionId}`);
                        if (refreshed.success && refreshed.data) setActiveSession(refreshed.data);
                        void refetchAll();
                        toast('Marked done & merged — available in Prometheus', 'success');
                      } else {
                        toast(result.error?.message ?? 'Cannot mark as done & merged', 'error');
                      }
                    }}
                  >
                    Marked Done &amp; Merged
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
